import { useEffect, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import {
  isUkPhone,
  normalisePhone,
  type SecondFactor,
  type User,
  type UserRole,
} from '@mads/shared'
import { ConfirmDialog, ErrorNote, Loading } from '@/components/Feedback'
import { Select } from '@/components/Select'
import {
  useCreateUser,
  useDeleteUser,
  useResendInvite,
  useUpdateUser,
  useUsers,
} from '@/lib/queries'
import { useCurrentUser, useIsAdmin } from '@/lib/session'

/**
 * Admin-only (spec W-05). Invite committee members, set roles, and remove
 * access. This screen exists on web only — mobile omits it by design (M-05).
 */
/**
 * What the Two-step column says, and whether it is worth noticing (issue #58).
 *
 * "None" is only an attention state on an installation that could do better. On
 * one with Twilio switched off — a checkout, CI, or a deployment without
 * credentials — every row would read None, and brass on every row is
 * decoration, which the design rules rule out. The reason goes above the table
 * instead.
 */
const SECOND_FACTOR_LABELS: Record<SecondFactor, string> = {
  passkey: 'Passkey',
  sms: 'Text message',
  none: 'None',
}

export function Users() {
  const isAdmin = useIsAdmin()
  const current = useCurrentUser()
  const { data, isPending, error } = useUsers(isAdmin)

  const create = useCreateUser()
  const update = useUpdateUser()
  const remove = useDeleteUser()
  const resendInvite = useResendInvite()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [confirming, setConfirming] = useState<User | null>(null)

  if (!isAdmin) {
    return (
      <>
        <h1 className="page-title">Users</h1>
        <p className="empty-note">Only admins can manage committee members.</p>
      </>
    )
  }

  if (isPending) return <Loading what="Loading users" />
  if (error !== null) return <ErrorNote error={error} />

  const { users, smsConfigured } = data

  return (
    <>
      <div className="stocktake-head">
        <h1 className="page-title" style={{ margin: 0 }}>
          Users
        </h1>
        <button type="button" className="btn btn-primary" onClick={() => setDialogOpen(true)}>
          + Add user
        </button>
      </div>
      <p className="page-lede">
        There is no open sign-up — committee accounts are provisioned here.
      </p>

      {!smsConfigured && (
        <p className="empty-note">
          Text-message verification isn&rsquo;t set up on this installation, so no account can be
          sent a code. Anyone who wants a second step has to register a passkey from Your account.
        </p>
      )}

      <ErrorNote error={update.error ?? remove.error} />

      <div className="table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Two-step</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const isSelf = current?.id === user.id

              return (
                <tr key={user.id}>
                  <td>
                    <Link
                      to="/users/$userId"
                      params={{ userId: String(user.id) }}
                      className="row-link"
                    >
                      {user.name}
                    </Link>
                    {isSelf && <span className="text-muted"> (you)</span>}
                  </td>
                  <td className="text-muted">{user.email}</td>
                  <td style={{ minWidth: 140 }}>
                    <Select
                      compact
                      value={user.role}
                      aria-label={`Role for ${user.name}`}
                      isDisabled={isSelf}
                      onChange={(value) =>
                        update.mutate({ id: user.id, input: { role: value as UserRole } })
                      }
                      options={[
                        { value: 'Admin', label: 'Admin' },
                        { value: 'Crew', label: 'Crew' },
                      ]}
                    />
                  </td>
                  <td>
                    <span className={user.active ? 'tag tag-accent' : 'tag tag-neutral'}>
                      {user.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {/* The longest label is two words, and a tag broken across
                      two lines reads as two tags. */}
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <span
                      className={
                        smsConfigured && user.secondFactor === 'none'
                          ? 'tag tag-accent-2'
                          : 'tag tag-neutral'
                      }
                    >
                      {SECOND_FACTOR_LABELS[user.secondFactor]}
                    </span>
                  </td>
                  <td>
                    <div className="action-row">
                      <button
                        type="button"
                        className="btn btn-ghost"
                        disabled={isSelf}
                        onClick={() =>
                          update.mutate({ id: user.id, input: { active: !user.active } })
                        }
                      >
                        {user.active ? 'Deactivate' : 'Reactivate'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => resendInvite.mutate(user.id)}
                      >
                        {resendInvite.isSuccess && resendInvite.variables === user.id
                          ? 'Sent'
                          : 'Resend invite'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        disabled={isSelf}
                        onClick={() => setConfirming(user)}
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {dialogOpen && (
        <AddUserDialog
          busy={create.isPending}
          error={create.error}
          onCancel={() => setDialogOpen(false)}
          onSubmit={(input) => {
            create.mutate(input, { onSuccess: () => setDialogOpen(false) })
          }}
        />
      )}

      {confirming !== null && (
        <ConfirmDialog
          title="Remove this user?"
          body={`${confirming.name} will lose access to the inventory immediately.`}
          confirmLabel="Remove"
          busy={remove.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            remove.mutate(confirming.id, { onSuccess: () => setConfirming(null) })
          }}
        />
      )}
    </>
  )
}

function AddUserDialog({
  busy,
  error,
  onCancel,
  onSubmit,
}: {
  busy: boolean
  error: unknown
  onCancel: () => void
  onSubmit: (input: { name: string; email: string; phone?: string; role: UserRole }) => void
}) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [role, setRole] = useState<UserRole>('Crew')
  const [validationError, setValidationError] = useState<string | null>(null)

  // Focus the first field on open, put focus back on the trigger on close, and
  // let Escape cancel — same shape as ConfirmDialog, so the keyboard works the
  // way the `role="dialog"` markup promises.
  const nameRef = useRef<HTMLInputElement>(null)
  const [trigger] = useState<HTMLElement | null>(() => document.activeElement as HTMLElement | null)

  useEffect(() => {
    nameRef.current?.focus()
    return () => {
      trigger?.focus()
    }
  }, [trigger])

  return (
    <div className="dialog-backdrop" onClick={onCancel}>
      <form
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Add a user"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !busy) {
            onCancel()
          }
        }}
        onSubmit={(event) => {
          event.preventDefault()

          // Empty is fine (issue #58) — an account without a number is a
          // choice, and the note beside the box says what it costs. What is
          // still refused is a number we could not dial, checked against the
          // same pattern the API uses so the message arrives without a round
          // trip.
          const typed = phone.trim()

          if (typed !== '' && !isUkPhone(typed)) {
            setValidationError(
              'Enter a UK phone number, for example 07700 900123 or +447700900123.',
            )
            return
          }

          setValidationError(null)
          onSubmit({ name, email, phone: typed === '' ? '' : normalisePhone(typed), role })
        }}
      >
        <div className="dialog-title">Add a user</div>

        <div className="dialog-body">
          {validationError !== null && (
            <div className="error-note" role="alert">
              {validationError}
            </div>
          )}
          <ErrorNote error={error} />

          <div className="field">
            <label htmlFor="user-name">Name</label>
            <input
              id="user-name"
          autoComplete="off"
          data-1p-ignore="true"
          data-lpignore="true"
              ref={nameRef}
              className="input"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="user-email">Email</label>
            <input
              id="user-email"
              className="input"
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <span className="text-muted" style={{ fontSize: 12 }}>
              We&rsquo;ll email them a link to set their own password.
            </span>
          </div>

          <div className="field">
            <label htmlFor="user-phone">Phone number (optional)</label>
            <input
              id="user-phone"
              className="input"
              type="tel"
              inputMode="tel"
              placeholder="+447700900123"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
            <span className="text-muted" style={{ fontSize: 12 }}>
              UK number, dialled or in international form — 07700 900123 or
              +447700900123. Leave it empty and no code can be texted to them:
              they&rsquo;ll sign in on their password alone unless they register a passkey.
            </span>
          </div>

          <div className="field">
            <label id="user-role-label">Role</label>
            <div className="stack" role="radiogroup" aria-labelledby="user-role-label">
              {(['Admin', 'Crew'] as const).map((option) => (
                <label className="radio" key={option} style={{ marginRight: 'var(--space-4)' }}>
                  <input
                    type="radio"
                    name="user-role"
                    checked={role === option}
                    onChange={() => setRole(option)}
                  />
                  <span className="dot" />
                  {option}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="dialog-actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Adding…' : 'Add user'}
          </button>
        </div>
      </form>
    </div>
  )
}
